import { FormattedMessage, useIntl } from 'react-intl';

import type { VehicleStateConfig } from '../../types';
import VariableCategoryGrid from '../VariableCard/VariableCategoryGrid';
import DatabaseVariablesPanel from './DatabaseVariablesPanel';

interface DiagnosticsSectionProps {
    stateConfig: VehicleStateConfig[];
    currentState?: Record<string, number | string>;
    mileage: number | null;
}

const StateVariableCard = ({
    config,
    currentValue,
}: {
    config: VehicleStateConfig;
    currentValue?: number | string;
}) => {
    const delta = config.increment ?? (config.decrement ? -config.decrement : null);

    return (
        <div className="panel panel-default margin-bottom-0">
            <div className="panel-body padding-10">
                <div className="display-flex justify-content-between align-items-center margin-bottom-5">
                    <span className="text-size-11 text-bold text-uppercase">{config.name}</span>
                    {delta !== null && (
                        <span className={`label label-condensed ${delta > 0 ? 'label-success' : 'label-danger'}`}>
                            {delta > 0 ? '↑' : '↓'} {Math.abs(delta)}
                        </span>
                    )}
                </div>
                <div
                    className={[
                        'display-flex justify-content-between align-items-end',
                        'border-top border-color-lighter padding-top-5',
                    ].join(' ')}
                >
                    <div>
                        <div className="text-size-9 text-color-gray text-bold text-uppercase">
                            <FormattedMessage id="intl-msg:diagnostics.init" />
                        </div>
                        <span className="text-size-12 font-family-mono text-color-gray">{config.initial}</span>
                    </div>
                    <div className="text-right">
                        <div className="text-size-9 text-color-gray text-bold text-uppercase margin-bottom-3">
                            <FormattedMessage id="intl-msg:diagnostics.currentLimit" />
                        </div>
                        <div
                            className={[
                                'bg-lightest padding-5 rounded border border-color-light',
                                'font-family-mono text-size-14 text-bold display-inline-block',
                            ].join(' ')}
                        >
                            <span className="text-color-primary">{currentValue ?? '—'}</span>
                            {config.limit !== null && (
                                <>
                                    <span className="text-color-gray margin-x-3">/</span>
                                    <span className="text-color-danger">{config.limit}</span>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const DiagnosticsSection = ({ stateConfig, currentState, mileage }: DiagnosticsSectionProps) => {
    const intl = useIntl();
    return (
        <div>
            <div className="margin-bottom-15">
                <VariableCategoryGrid
                    icon="rioglyph-circle-stack"
                    iconColor="text-color-warning"
                    label={intl.formatMessage({ id: 'intl-msg:diagnostics.databaseVariables' })}
                    prefix="database"
                >
                    <DatabaseVariablesPanel mileage={mileage} />
                </VariableCategoryGrid>
            </div>

            {stateConfig.length > 0 && (
                <VariableCategoryGrid
                    icon="rioglyph-stats"
                    iconColor="text-color-primary"
                    label={intl.formatMessage({ id: 'intl-msg:diagnostics.stateVariables' })}
                    prefix="state"
                >
                    {stateConfig.map((config) => (
                        <StateVariableCard
                            key={config.name}
                            config={config}
                            currentValue={currentState?.[config.name]}
                        />
                    ))}
                </VariableCategoryGrid>
            )}
        </div>
    );
};

export default DiagnosticsSection;
